import { useEffect } from 'react';
import { GridApi } from 'ag-grid-community';
import { UseControlledAGGridProps } from '../types';

const isGridGeneratedColumnId = (colId: string): boolean =>
  colId.startsWith('ag-Grid-');

export const useVisibleColumns = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>,
  api: GridApi | null
) => {
  const visibleColumnIds = props.columnIds;
  const visibleColumnsEnabled = visibleColumnIds !== undefined;

  useEffect(() => {
    if (!api) {
      return;
    }

    // Get all columns including hidden ones
    const allColumns = api.getAllGridColumns();
    if (!visibleColumnsEnabled) {
      // If visibleColumnIds is an empty array, show all column
      const columnState = allColumns.map(col => ({
        colId: col.getColId(),
        hide: false,
      }));
      api.applyColumnState({ state: columnState });
      return;
    }

    const visibleSet = new Set(visibleColumnIds.map(String));

    const columnState = allColumns
      .filter(col => !isGridGeneratedColumnId(col.getColId()))
      .map(col => ({
        colId: col.getColId(),
        hide: !visibleSet.has(col.getColId()),
      }));

    api.applyColumnState({ state: columnState });
  }, [api, visibleColumnsEnabled, visibleColumnIds]);

  return {
    visibleColumnIds: visibleColumnsEnabled ? visibleColumnIds : null,
  };
};
